import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { Notice } from '@/components/admin/Notice';
import {
  fetchNotificationTemplates,
  fetchPresets,
  updatePreset,
  type RuleDef,
} from '@/features/notifications/api';
import { PresetRulesEditor } from '@/features/notifications/PresetRulesEditor';
import { ApiError } from '@/lib/api/http';

/** 通知プリセットのルール編集（独立ページ / D-292）。 */
export function NotificationPresetEditPage(): ReactElement {
  const { presetId } = useParams<{ presetId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const presetsQuery = useQuery({ queryKey: ['notification-presets'], queryFn: fetchPresets });
  const templatesQuery = useQuery({
    queryKey: ['notification-templates'],
    queryFn: fetchNotificationTemplates,
  });

  const preset = (presetsQuery.data ?? []).find((p) => p.id === presetId);

  const [name, setName] = useState('');
  const [rules, setRules] = useState<RuleDef[]>([]);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!preset || hydrated) return;
    setName(preset.name);
    setRules(preset.definition.rules ?? []);
    setJsonText(JSON.stringify(preset.definition, null, 2));
    setHydrated(true);
  }, [preset, hydrated]);

  async function onSave(): Promise<void> {
    if (!presetId) return;
    setBusy(true);
    setError(null);
    try {
      const definition = jsonMode
        ? (JSON.parse(jsonText) as { rules: RuleDef[] })
        : { rules };
      await updatePreset(presetId, { name: name.trim(), definition });
      await queryClient.invalidateQueries({ queryKey: ['notification-presets'] });
      navigate('/masters/notification-presets', {
        state: {
          notice:
            'プリセットを保存しました。既存イベントの配信予定は変わりません（新規展開分から反映）。',
        },
      });
    } catch (err) {
      if (err instanceof SyntaxError) setError('JSONの形式が不正です。');
      else setError(err instanceof ApiError ? err.message : '保存に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  if (presetsQuery.isPending) {
    return <p className="text-wptext-muted">読み込み中…</p>;
  }
  if (presetsQuery.isError) {
    return (
      <Notice kind="error" dismissible={false}>
        {presetsQuery.error instanceof ApiError
          ? presetsQuery.error.message
          : '情報を読み込めませんでした。'}
      </Notice>
    );
  }
  if (!preset) {
    return (
      <Notice kind="error" dismissible={false}>
        プリセットが見つかりません。
        <Link to="/masters/notification-presets" className="ml-2 underline">
          一覧へ戻る
        </Link>
      </Notice>
    );
  }

  return (
    <>
      <PageHeader
        title={`${preset.name} の編集`}
        description="配信ルールを編集します。保存しても、すでに作られたイベントの配信予定は変わりません。これから作成・公開するイベントから反映されます。"
        actions={
          <Link to="/masters/notification-presets" className="btn-secondary">
            一覧へ戻る
          </Link>
        }
      />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <div className="mb-4 rounded-sm border border-wpborder bg-white px-3 py-3">
        <p className="mb-2 text-wptext-muted">
          コード: <code>{preset.code}</code>
        </p>
        <label className="mb-3 block max-w-md">
          <span className="mb-1.5 block text-wptext-muted">名称</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
        <div className="mb-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              if (!jsonMode) {
                setJsonText(JSON.stringify({ rules }, null, 2));
              }
              setJsonMode((m) => !m);
            }}
            className="btn-secondary"
          >
            {jsonMode ? 'フォームで編集' : 'JSONで編集（補助）'}
          </button>
          <button
            type="button"
            disabled={busy || !name.trim()}
            onClick={() => void onSave()}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            保存する
          </button>
        </div>
      </div>

      <PresetRulesEditor
        rules={rules}
        onChange={setRules}
        templates={templatesQuery.data ?? []}
        jsonMode={jsonMode}
        jsonText={jsonText}
        onJsonTextChange={setJsonText}
      />
    </>
  );
}
